import { Box, HStack } from '@elkaso-app/web-design';
import { CmsCatalogCategoryDto } from 'apis/cms-catalog-categories';
import { useEffect, useRef } from 'react';
import { CascadeCategoryColumn } from './cascade-category-column';

/** Tree depth is capped at 4. No children exist below depth 4, so no column is rendered for a depth-4 selection. */
const MAX_DEPTH = 4;

export interface CascadeCategorySelectProps {
  /** Drill-down path. `selectedPath[0]` is the root pick, `selectedPath[last]` is the current leaf pick. */
  selectedPath: CmsCatalogCategoryDto[];
  onSelect: (path: CmsCatalogCategoryDto[]) => void;
}

/**
 * Finder-style horizontal column cascade.
 *
 * Renders columns left to right, with roots at column 0 and each selection's children appearing
 * in the next column. No trailing empty column is rendered once the user picks a depth-4 item.
 */
export const CascadeCategorySelect = ({ selectedPath, onSelect }: CascadeCategorySelectProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Always start with roots. For each selected item with depth < MAX_DEPTH, append a children column.
  const columnParentIds: Array<number | undefined> = [undefined];
  for (const p of selectedPath) {
    if (p.depth < MAX_DEPTH) columnParentIds.push(p.id);
  }

  // Auto-scroll to the newest (rightmost) column whenever depth changes so drilling feels L→R.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
  }, [selectedPath.length]);

  const handleColumnSelect = (columnIndex: number, item: CmsCatalogCategoryDto) => {
    const newPath = [...selectedPath.slice(0, columnIndex), item];
    onSelect(newPath);
  };

  return (
    <Box
      ref={scrollRef}
      height='420px'
      overflowX='auto'
      overflowY='hidden'
      borderWidth='1px'
      borderColor='gray.200'
      borderRadius='md'>
      <HStack spacing={0} align='stretch' height='100%' minW='fit-content'>
        {columnParentIds.map((parentId, i) => (
          <CascadeCategoryColumn
            key={`${i}-${parentId ?? 'root'}`}
            parentId={parentId}
            selectedId={selectedPath[i]?.id}
            onSelect={(item) => handleColumnSelect(i, item)}
            columnIndex={i}
          />
        ))}
      </HStack>
    </Box>
  );
};
